import { getPublicProfile } from '../../../services/social/api';
import {
  Activity,
  Trip,
  getChecklist,
  getTripMembers,
} from '../../../services/trips/api/tripsApi';
import { PdfTraveller, buildTripPdfHtml } from './tripPdf';

/**
 * Print the trip, which is how a browser saves a PDF.
 *
 * The document goes into a hidden iframe and that iframe prints itself, so
 * "Save as PDF" in the print dialog produces the file. No library: the browser
 * already renders HTML to PDF better than a bundled one would, keeps the text
 * selectable and searchable, and costs nothing to ship. The trade is the
 * dialog, which is why the button says "Print / Save as PDF" rather than
 * promising a download.
 *
 * Returns a readable outcome rather than throwing — every failure here is
 * something to tell the person calmly.
 */

export interface PrintResult {
  ok: boolean;
  message: string;
}

const ROLE_LABEL = {
  owner: 'Owner',
  collaborator: 'Can edit',
  member: 'View only',
} as const;

/**
 * The roster, resolved to names. Member rows carry ids and roles only, so the
 * names come from public profiles. A profile that will not load becomes
 * "Traveller" rather than a gap: a missing display name does not make someone
 * not going.
 */
async function travellersOf(tripId: string): Promise<PdfTraveller[]> {
  const roster = await getTripMembers(tripId).catch(() => ({ ownerId: '', members: [] }));
  const ids = [roster.ownerId, ...roster.members.map((member) => member.userId)].filter(Boolean);

  return Promise.all(
    ids.map(async (id) => {
      const profile = await getPublicProfile(id).catch(() => null);
      const name =
        [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') ||
        profile?.nickname ||
        'Traveller';
      const role =
        id === roster.ownerId
          ? 'owner'
          : roster.members.find((member) => member.userId === id)?.role === 'collaborator'
            ? 'collaborator'
            : 'member';
      return { name, role: ROLE_LABEL[role] };
    }),
  );
}

/**
 * Hand the document to the browser's print pipeline.
 *
 * An iframe rather than a new window: a popup blocker will stop `window.open`
 * on a click it does not like, and a printed tab leaves the person somewhere
 * they have to navigate back from.
 */
function printHtml(html: string): void {
  const frame = document.createElement('iframe');
  // Off-screen rather than display:none — a frame with no box does not always
  // lay its content out, and an unlaid-out document prints blank.
  frame.setAttribute('aria-hidden', 'true');
  frame.style.position = 'fixed';
  frame.style.right = '100%';
  frame.style.bottom = '100%';
  frame.style.width = '1px';
  frame.style.height = '1px';
  frame.style.border = '0';

  frame.onload = () => {
    const view = frame.contentWindow;
    if (!view) return;

    // Clean up once the dialog closes. The timeout is the fallback: Safari
    // does not always fire afterprint, and an orphaned iframe per export would
    // accumulate for as long as the page is open.
    const remove = () => frame.remove();
    view.addEventListener('afterprint', remove, { once: true });
    window.setTimeout(remove, 60_000);

    view.focus();
    view.print();
  };

  /**
   * `srcdoc`, not `document.write` after appending.
   *
   * Appending first loads `about:blank`, and THAT fires `onload` — before any
   * write lands. Printing from there prints an empty page, which is what the
   * first version of this did. Setting the content before insertion means the
   * load event belongs to the document we actually want.
   */
  frame.srcdoc = html;
  document.body.appendChild(frame);
}

export async function printTripPdf(trip: Trip, activities: Activity[]): Promise<PrintResult> {
  try {
    /**
     * Fetched here rather than taken from the page.
     *
     * The roster and the checklist load with their own tabs, so someone who
     * prints without opening those has never loaded either — and a document
     * quietly missing two of its four sections is worse than a slower button.
     * Both endpoints already exist; neither is new work for the server.
     */
    const [travellers, checklist] = await Promise.all([
      travellersOf(String(trip.id)),
      getChecklist(trip.id).catch(() => []),
    ]);

    printHtml(buildTripPdfHtml({ trip, activities, travellers, checklist }));
    return { ok: true, message: 'Opening the print dialog…' };
  } catch (error) {
    return {
      ok: false,
      message: error instanceof Error ? error.message : 'Could not build the document.',
    };
  }
}
